export const MIN_GAMEPLAY_LEVEL = 1;
export const MAX_GAMEPLAY_LEVEL = 50;
export const REALM_LAYER_COUNT = 10;
export const ASCENSION_SPIRIT_POWER_TARGET = 96_000;
export const MAX_LEVEL_SPIRIT_POWER_TARGET = 92_800;
export const LEVEL_SPIRIT_POWER_CURVE_STEEPNESS = 7;
export function getRequiredSpiritPowerForGameplayLevel(level) {
    const normalizedLevel = normalizeGameplayLevel(level);
    if (normalizedLevel <= MIN_GAMEPLAY_LEVEL) {
        return 0;
    }
    const progress = (normalizedLevel - MIN_GAMEPLAY_LEVEL) /
        (MAX_GAMEPLAY_LEVEL - MIN_GAMEPLAY_LEVEL);
    const exponentialProgress = (Math.exp(LEVEL_SPIRIT_POWER_CURVE_STEEPNESS * progress) - 1) /
        (Math.exp(LEVEL_SPIRIT_POWER_CURVE_STEEPNESS) - 1);
    return Math.round(MAX_LEVEL_SPIRIT_POWER_TARGET * exponentialProgress);
}
export const REALM_CONFIGS = [
    {
        id: "qi",
        name: "练气",
        levelStart: 1,
        levelEnd: 10,
        spiritStart: getRequiredSpiritPowerForGameplayLevel(1),
        nextRealmSpirit: getRequiredSpiritPowerForGameplayLevel(11),
        layerSpiritStep: (getRequiredSpiritPowerForGameplayLevel(11) -
            getRequiredSpiritPowerForGameplayLevel(1)) /
            REALM_LAYER_COUNT,
        attackRadiusStart: 60,
        attackRadiusEnd: 95,
        attackArcDegreesStart: 45,
        attackArcDegreesEnd: 65,
        color: "#9ce6ff",
    },
    {
        id: "foundation",
        name: "筑基",
        levelStart: 11,
        levelEnd: 20,
        spiritStart: getRequiredSpiritPowerForGameplayLevel(11),
        nextRealmSpirit: getRequiredSpiritPowerForGameplayLevel(21),
        layerSpiritStep: (getRequiredSpiritPowerForGameplayLevel(21) -
            getRequiredSpiritPowerForGameplayLevel(11)) /
            REALM_LAYER_COUNT,
        attackRadiusStart: 105,
        attackRadiusEnd: 155,
        attackArcDegreesStart: 70,
        attackArcDegreesEnd: 92,
        color: "#70e0c2",
    },
    {
        id: "core",
        name: "结丹",
        levelStart: 21,
        levelEnd: 30,
        spiritStart: getRequiredSpiritPowerForGameplayLevel(21),
        nextRealmSpirit: getRequiredSpiritPowerForGameplayLevel(31),
        layerSpiritStep: (getRequiredSpiritPowerForGameplayLevel(31) -
            getRequiredSpiritPowerForGameplayLevel(21)) /
            REALM_LAYER_COUNT,
        attackRadiusStart: 170,
        attackRadiusEnd: 245,
        attackArcDegreesStart: 98,
        attackArcDegreesEnd: 122,
        color: "#f7d98a",
    },
    {
        id: "nascent",
        name: "元婴",
        levelStart: 31,
        levelEnd: 40,
        spiritStart: getRequiredSpiritPowerForGameplayLevel(31),
        nextRealmSpirit: getRequiredSpiritPowerForGameplayLevel(41),
        layerSpiritStep: (getRequiredSpiritPowerForGameplayLevel(41) -
            getRequiredSpiritPowerForGameplayLevel(31)) /
            REALM_LAYER_COUNT,
        attackRadiusStart: 265,
        attackRadiusEnd: 360,
        attackArcDegreesStart: 128,
        attackArcDegreesEnd: 152,
        color: "#b8d8ff",
    },
    {
        id: "spirit",
        name: "化神",
        levelStart: 41,
        levelEnd: 50,
        spiritStart: getRequiredSpiritPowerForGameplayLevel(41),
        nextRealmSpirit: ASCENSION_SPIRIT_POWER_TARGET,
        layerSpiritStep: (ASCENSION_SPIRIT_POWER_TARGET -
            getRequiredSpiritPowerForGameplayLevel(41)) /
            REALM_LAYER_COUNT,
        attackRadiusStart: 390,
        attackRadiusEnd: 520,
        attackArcDegreesStart: 156,
        attackArcDegreesEnd: 175,
        color: "#fff1a8",
    },
];
export const LEVEL_COMBAT_CONFIGS = Object.fromEntries(Array.from({ length: MAX_GAMEPLAY_LEVEL }, (_, index) => {
    const level = index + 1;
    return [level, createLevelCombatConfig(level)];
}));
export function normalizeGameplayLevel(value) {
    if (!Number.isFinite(value)) {
        return MIN_GAMEPLAY_LEVEL;
    }
    const rounded = Math.round(value ?? MIN_GAMEPLAY_LEVEL);
    if (rounded <= MIN_GAMEPLAY_LEVEL) {
        return MIN_GAMEPLAY_LEVEL;
    }
    if (rounded >= MAX_GAMEPLAY_LEVEL) {
        return MAX_GAMEPLAY_LEVEL;
    }
    return rounded;
}
export function getLevelCombatConfig(level) {
    return LEVEL_COMBAT_CONFIGS[normalizeGameplayLevel(level)];
}
export function getRealmLayerInfo(level) {
    const normalizedLevel = normalizeGameplayLevel(level);
    const realm = getRealmConfigForLevel(normalizedLevel);
    const layer = normalizedLevel - realm.levelStart + 1;
    const currentLayerSpirit = getRequiredSpiritPowerForGameplayLevel(normalizedLevel);
    const nextLayerSpirit = normalizedLevel >= MAX_GAMEPLAY_LEVEL
        ? null
        : getRequiredSpiritPowerForGameplayLevel(normalizedLevel + 1);
    return {
        realm,
        level: normalizedLevel,
        layer,
        label: `${realm.name} ${layer.toString()} 层`,
        currentLayerSpirit,
        nextLayerSpirit,
    };
}
export function getRealmConfigForLevel(level) {
    const normalizedLevel = normalizeGameplayLevel(level);
    return (REALM_CONFIGS.find((realm) => normalizedLevel >= realm.levelStart &&
        normalizedLevel <= realm.levelEnd) ?? REALM_CONFIGS[0]);
}
function createLevelCombatConfig(level) {
    const normalizedLevel = normalizeGameplayLevel(level);
    const realm = getRealmConfigForLevel(normalizedLevel);
    const layer = normalizedLevel - realm.levelStart + 1;
    const progress = (layer - 1) / (REALM_LAYER_COUNT - 1);
    const globalProgress = (normalizedLevel - MIN_GAMEPLAY_LEVEL) /
        (MAX_GAMEPLAY_LEVEL - MIN_GAMEPLAY_LEVEL);
    return {
        level: normalizedLevel,
        realmId: realm.id,
        realmName: realm.name,
        layer,
        attackRadius: interpolate(realm.attackRadiusStart, realm.attackRadiusEnd, progress),
        attackArcDegrees: interpolate(realm.attackArcDegreesStart, realm.attackArcDegreesEnd, progress),
        minSpeed: interpolate(60, 98, globalProgress),
        maxSpeed: interpolate(60, 98, globalProgress),
        boostSpeed: interpolate(150, 235, globalProgress),
        turnRate: interpolate(Math.PI * 1.25, Math.PI * 0.62, globalProgress),
        inertia: interpolate(0.12, 0.62, globalProgress),
        attackWindupMs: getAttackWindupMs(realm.id, progress),
        attackRecoveryMs: interpolate(820, 1040, globalProgress),
        attackMissRecoveryMs: interpolate(420, 760, globalProgress),
    };
}
function getAttackWindupMs(realmId, progress) {
    switch (realmId) {
        case "qi":
            return interpolate(300, 282, progress);
        case "foundation":
            return interpolate(260, 248, progress);
        case "core":
            return interpolate(240, 228, progress);
        case "nascent":
            return interpolate(220, 208, progress);
        case "spirit":
            return interpolate(200, 188, progress);
    }
}
function interpolate(start, end, progress) {
    return start + (end - start) * progress;
}
